import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { RefreshCw, Search } from 'lucide-react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger
} from './ui/alert-dialog';
import { toast } from 'sonner';
import { ScoreBadge, CRITERIO_LABELS, TicketLink, ProvisionalBadge, isPilotLocked, PILOT_AGREEMENT_THRESHOLD } from './AgentQualityCalibration';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AgentNotes = ({ token, instances, agreementRate = 0, totalCalibrated = 0 }) => {
  const [agents, setAgents] = useState([]);
  const [filters, setFilters] = useState({ instance: '', agentId: '', dateFrom: '', dateTo: '' });
  const [note, setNote] = useState(null);
  const [loading, setLoading] = useState(false);

  const [ticketIdInput, setTicketIdInput] = useState('');
  const [scoringOndemand, setScoringOndemand] = useState(false);
  const [ondemandResult, setOndemandResult] = useState(null);

  const loadAgents = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/agent-quality/calibration/agents`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      setAgents(data.agents || []);
    } catch (error) {
      console.error('Error cargando agentes:', error);
    }
  }, [token]);

  const loadNote = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.instance) params.append('instance', filters.instance);
      if (filters.agentId) params.append('agent_id', filters.agentId);
      if (filters.dateFrom) params.append('date_from', filters.dateFrom);
      if (filters.dateTo) params.append('date_to', filters.dateTo);
      const response = await fetch(`${API_URL}/api/agent-quality/calibration/agent-notes?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      setNote(data);
    } catch (error) {
      console.error('Error cargando nota de agente:', error);
      toast.error('Error cargando la nota agregada');
    } finally {
      setLoading(false);
    }
  }, [token, filters]);

  useEffect(() => { loadAgents(); }, [loadAgents]);
  useEffect(() => { loadNote(); }, [loadNote]);

  const handleScoreOndemand = async () => {
    if (!ticketIdInput.trim()) return;
    setScoringOndemand(true);
    setOndemandResult(null);
    try {
      const response = await fetch(
        `${API_URL}/api/agent-quality/calibration/score-ondemand/${ticketIdInput.trim()}`,
        { method: 'POST', headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await response.json().catch(() => null);
      if (!response.ok) {
        const friendly = data?.detail?.includes('HUBSPOT_API_TOKEN')
          ? 'La integración con HubSpot no está configurada en este entorno'
          : (data?.detail || 'Error scoreando el ticket');
        throw new Error(friendly);
      }
      setOndemandResult(data);
      if (data.status === 'already_scored') {
        toast.info('Este ticket ya estaba scoreado - no se volvió a evaluar');
      } else {
        toast.success('Ticket scoreado on-demand. No se suma a la nota oficial hasta confirmarlo.');
      }
    } catch (error) {
      toast.error(error.message || 'Error scoreando el ticket');
    } finally {
      setScoringOndemand(false);
    }
  };

  const handleConfirmForNote = async () => {
    const tid = ondemandResult?.score?.ticket_id;
    if (!tid) return;
    try {
      const response = await fetch(
        `${API_URL}/api/agent-quality/calibration/score-ondemand/${tid}/confirm`,
        { method: 'PUT', headers: { Authorization: `Bearer ${token}` } }
      );
      if (!response.ok) throw new Error('Error confirmando');
      toast.success('Ticket sumado a la nota oficial del agente');
      setOndemandResult({ ...ondemandResult, score: { ...ondemandResult.score, confirmed_for_note: true } });
      loadNote();
    } catch (error) {
      toast.error(error.message || 'Error confirmando');
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="font-semibold text-gray-900">Nota por Agente</h3>
        <p className="text-sm text-gray-500">
          Promedio de la muestra semanal ya scoreada (3→10 tickets/agente) - no del 100% de sus tickets.
        </p>
      </div>

      {/* Filtros combinables */}
      <Card>
        <CardContent className="pt-4 grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <label className="text-xs text-gray-500">Instancia</label>
            <select
              className="w-full border rounded-md px-3 py-2 text-sm"
              value={filters.instance}
              onChange={(e) => setFilters({ ...filters, instance: e.target.value })}
              data-testid="agent-notes-instance-filter"
            >
              <option value="">Todas</option>
              {(instances || []).map((inst) => (
                <option key={inst.slug} value={inst.slug}>{inst.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs text-gray-500">Agente</label>
            <select
              className="w-full border rounded-md px-3 py-2 text-sm"
              value={filters.agentId}
              onChange={(e) => setFilters({ ...filters, agentId: e.target.value })}
              data-testid="agent-notes-agent-filter"
            >
              <option value="">Todos</option>
              {agents.map((a) => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs text-gray-500">Desde</label>
            <Input
              type="date"
              value={filters.dateFrom}
              onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
              data-testid="agent-notes-date-from"
            />
          </div>
          <div>
            <label className="text-xs text-gray-500">Hasta</label>
            <Input
              type="date"
              value={filters.dateTo}
              onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
              data-testid="agent-notes-date-to"
            />
          </div>
        </CardContent>
      </Card>

      {/* Nota agregada */}
      {loading ? (
        <p className="text-sm text-gray-400" data-testid="agent-notes-loading">Calculando nota...</p>
      ) : !note || note.n === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-gray-400" data-testid="agent-notes-empty">
            No hay tickets scoreados que coincidan con estos filtros.
          </CardContent>
        </Card>
      ) : (
        <Card data-testid="agent-notes-result">
          <CardHeader className="pb-3">
            <CardTitle className="text-base" data-testid="agent-notes-title">
              Nota de la muestra de {note.n} tickets
            </CardTitle>
            <CardDescription>
              Promedio simple de la muestra ya scoreada (usa el score calibrado por un humano cuando existe, sino el del LLM; cada score ya combina sus 6 ejes con su propio peso).
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-4">
              <ScoreBadge score={note.note_global} size="large" />
              <ProvisionalBadge agreementRate={agreementRate} totalCalibrated={totalCalibrated} />
              <span className="text-sm text-gray-500">Nota global de la muestra</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {Object.entries(note.note_por_eje).map(([codigo, valor]) => (
                <div key={codigo} className="border rounded-lg p-2 text-sm" data-testid={`agent-notes-eje-${codigo}`}>
                  <p className="font-medium">{CRITERIO_LABELS[codigo]?.icon} {CRITERIO_LABELS[codigo]?.name}</p>
                  <p className="text-gray-500">{valor}</p>
                </div>
              ))}
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-700 mb-2">Tickets de la muestra ({note.tickets.length})</p>
              <div className="max-h-72 overflow-y-auto border rounded-lg divide-y">
                {note.tickets.map((t) => (
                  <div key={t.ticket_id} className="flex items-center justify-between px-3 py-2 text-sm" data-testid={`agent-notes-ticket-${t.ticket_id}`}>
                    <TicketLink ticketId={t.ticket_id} />
                    <span className="text-gray-400 text-xs">{t.fecha_ticket || 'sin fecha'}</span>
                    <span className="text-gray-500 truncate max-w-[120px]">{t.agent_name || 'Sin agente'}</span>
                    <div className="flex items-center gap-2">
                      <ScoreBadge score={t.score_global_calibrado ?? t.score_global} size="sm" />
                      <ProvisionalBadge agreementRate={agreementRate} totalCalibrated={totalCalibrated} />
                      {t.is_on_demand && <Badge variant="outline" className="text-xs">On-demand</Badge>}
                      <Badge variant="outline" className="text-xs">
                        {t.calibration_status === 'calibrated' ? 'Calibrado' : 'Pendiente'}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Auditar un ticket puntual (fuera de la muestra) */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Auditar un Ticket Puntual</CardTitle>
          <CardDescription>
            ¿Querés revisar un ticket que NO está en la muestra? Se scorea individualmente (1 llamada al LLM) y NO se suma a la nota oficial salvo que lo confirmes.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-2">
            <Input
              className="flex-1"
              placeholder="ID de ticket de HubSpot (ej: 46387720217)"
              value={ticketIdInput}
              onChange={(e) => setTicketIdInput(e.target.value)}
              data-testid="ondemand-ticket-id-input"
            />
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button className="shrink-0" disabled={!ticketIdInput.trim() || scoringOndemand} data-testid="ondemand-score-btn">
                  <Search className="h-4 w-4 mr-1" />
                  Scorear este ticket
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>¿Scorear el ticket {ticketIdInput}?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Esto dispara una evaluación real con el LLM (costo y tiempo de un ticket individual).
                    El resultado NO se sumará a la nota oficial del agente salvo que lo confirmes explícitamente después.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="ondemand-confirm-cancel">Cancelar</AlertDialogCancel>
                  <AlertDialogAction onClick={handleScoreOndemand} data-testid="ondemand-confirm-accept">
                    Sí, scorear
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>

          {scoringOndemand && <p className="text-sm text-gray-400">Scoreando ticket, puede tardar unos segundos...</p>}

          {ondemandResult && (
            <div className="border rounded-lg p-3 space-y-2" data-testid="ondemand-result">
              <div className="flex items-center gap-3">
                <TicketLink ticketId={ondemandResult.score.ticket_id} />
                <ScoreBadge score={ondemandResult.score.score_global} />
                <Badge variant="outline" className="text-xs">
                  {ondemandResult.status === 'already_scored' ? 'Ya existía' : 'Scoreado ahora'}
                </Badge>
              </div>
              {ondemandResult.score.confirmed_for_note ? (
                <Badge className="bg-green-100 text-green-700" data-testid="ondemand-confirmed-badge">
                  Incluido en la nota oficial
                </Badge>
              ) : ondemandResult.score.is_on_demand ? (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button size="sm" variant="outline" data-testid="ondemand-include-note-btn">
                      Incluir en la nota oficial
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>¿Sumar este ticket a la nota oficial?</AlertDialogTitle>
                      <AlertDialogDescription>
                        Esto va a afectar el promedio agregado del agente. Solo hacelo si querés que esta auditoría puntual pase a formar parte de la muestra representativa.
                        {isPilotLocked(agreementRate, totalCalibrated) && (
                          <span className="block mt-2 font-semibold text-amber-700" data-testid="ondemand-pilot-warning">
                            ⚠️ PILOTO — IA sin validar (agreement {agreementRate}%, {totalCalibrated} casos calibrados).
                            Esta nota es PROVISORIA y no debe usarse para decisiones sobre agentes ni proveedores
                            hasta alcanzar agreement ≥{PILOT_AGREEMENT_THRESHOLD}% con muestra suficiente.
                          </span>
                        )}
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancelar</AlertDialogCancel>
                      <AlertDialogAction onClick={handleConfirmForNote} data-testid="ondemand-confirm-include-accept">
                        Sí, incluir
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              ) : null}
            </div>
          )}
        </CardContent>
      </Card>

      <Button variant="outline" size="sm" onClick={loadNote} disabled={loading} data-testid="agent-notes-refresh-btn">
        <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
        Actualizar
      </Button>
    </div>
  );
};

export default AgentNotes;
